import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormHelperText,
} from '@mui/material';
import { Event, EventLookup, FormData } from '../types';
import { eventApi } from '../services/api';
import { useTranslation } from '../i18n/TranslationContext';
import { computeSupplements, personTotal, signedMoney, supplementForOption, supplementSuffix, tipologiaAmount } from '../utils/supplements';

interface DynamicFormProps {
  event: Event;
  onSubmit: (data: FormData) => void;
  loading?: boolean;
  error?: string;
}

const DynamicForm: React.FC<DynamicFormProps> = ({ event, onSubmit, loading = false, error }) => {
  const { t, fmtMoney } = useTranslation();
  const [formData, setFormData] = useState<FormData>({});
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});
  const errorAlertRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && errorAlertRef.current) {
      errorAlertRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  useEffect(() => {
    setFormData(prev => {
      const next = { ...prev };
      event.lookups.forEach(lookup => {
        if (lookup.lookup.type === 'checkbox' && next[lookup.lookup_id] === undefined) {
          next[lookup.lookup_id] = 0;
        }
      });
      return next;
    });
  }, [event.lookups]);

  const handleInputChange = (fieldId: number | string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
    
    if (formErrors[fieldId.toString()]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldId]: ''
      }));
    }
  };

  const isFieldVisible = (lookup: EventLookup): boolean => {
    if (!lookup.visibile_condition || !lookup.condition_lookup_id || lookup.condition_value === null) {
      return true;
    }

    const conditionValue = formData[lookup.condition_lookup_id];
    if (conditionValue !== lookup.condition_value) {
      return false;
    }

    // Verifica ricorsiva: il campo padre deve essere a sua volta visibile
    const parentLookup = event.lookups.find(l => l.lookup_id === lookup.condition_lookup_id);
    if (parentLookup && !isFieldVisible(parentLookup)) {
      return false;
    }

    return true;
  };

  const validateForm = (): boolean => {
    const errors: { [key: string]: string } = {};

    // Validate required customer fields
    if (!formData.customer_name || formData.customer_name === '') {
      errors.customer_name = formData.is_ente ? t('form.err_denominazione', 'La denominazione è obbligatoria') : t('form.err_nome', 'Nome è obbligatorio');
    }
    if (!formData.customer_lastname || formData.customer_lastname === '') {
      errors.customer_lastname = formData.is_ente ? t('form.err_riferimento', 'Il riferimento è obbligatorio') : t('form.err_cognome', 'Cognome è obbligatorio');
    }
    if (!formData.customer_email || formData.customer_email === '') {
      errors.customer_email = t('form.err_email_req', 'Email è obbligatoria');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.customer_email as string)) {
      errors.customer_email = t('form.err_email', 'Email non valida');
    }
    if (!formData.customer_fiscal_code || formData.customer_fiscal_code === '') {
      errors.customer_fiscal_code = t('form.err_cf_req', 'Codice fiscale è obbligatorio');
    } else if ((formData.customer_fiscal_code as string).length < 16) {
      errors.customer_fiscal_code = t('form.err_cf_len', 'Il codice fiscale deve essere di 16 caratteri');
    } else if (!/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(formData.customer_fiscal_code as string)) {
      errors.customer_fiscal_code = t('form.err_cf', 'Codice fiscale non valido');
    }

    // Validate participation mode if event allows both
    if (event.participation_mode === 'ENTRAMBI' && !formData.participation_mode) {
      errors.participation_mode = t('form.err_participation', 'Modalità di partecipazione è obbligatoria');
    }

    event.lookups.forEach(lookup => {
      if (lookup.participant_scope === 'participants') return;
      if (lookup.show_if_ente === 1 && formData.is_ente !== true) return;
      if (isFieldVisible(lookup) && lookup.required === 1) {
        const value = formData[lookup.lookup_id];
        if (!value || value === '') {
          errors[lookup.lookup_id.toString()] = t('form.err_required', '{{field}} è obbligatorio', { field: lookup.lookup.name });
        }
      }
    });

    // Validate price selection if prices exist
    if (event.prices && event.prices.length > 0 && !formData.price_id) {
      errors.price_id = t('form.err_tipologia', 'Tipologia è obbligatoria');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validateForm()) {
      onSubmit(formData);
    } else {
      const firstError = document.querySelector('[class*="Mui-error"]') as HTMLElement;
      if (firstError) {
        firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const input = firstError.querySelector('input, select, textarea') as HTMLElement;
        if (input) input.focus();
      }
    }
  };

  const renderField = (lookup: EventLookup) => {
    if (!isFieldVisible(lookup)) {
      return null;
    }

    const fieldId = lookup.lookup_id;
    const value = formData[fieldId] || '';
    const hasError = !!formErrors[fieldId.toString()];
    const errorMessage = formErrors[fieldId.toString()];

    switch (lookup.lookup.type) {
      case 'select':
        return (
          <FormControl fullWidth key={fieldId} error={hasError} sx={{ mb: 2 }}>
            <InputLabel>{lookup.lookup.name}</InputLabel>
            <Select
              value={value}
              label={lookup.lookup.name}
              onChange={(e) => handleInputChange(fieldId, e.target.value as number)}
              required={lookup.required === 1}
            >
              <MenuItem value="">
                <em>{t('form.select_option', "Seleziona un'opzione")}</em>
              </MenuItem>
              {lookup.lookup.options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.option} {supplementSuffix(supplementForOption(lookup, option.id), t, fmtMoney)}
                </MenuItem>
              ))}
            </Select>
            {hasError && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {errorMessage}
              </Typography>
            )}
          </FormControl>
        );

      case 'textarea':
        return (
          <TextField
            key={fieldId}
            fullWidth
            multiline
            minRows={3}
            label={lookup.lookup.name}
            value={value}
            onChange={(e) => handleInputChange(fieldId, e.target.value)}
            required={lookup.required === 1}
            error={hasError}
            helperText={errorMessage}
            sx={{ mb: 2 }}
          />
        );

      case 'text':
      case 'email':
      case 'number':
      case 'date':
        return (
          <TextField
            key={fieldId}
            fullWidth
            label={lookup.lookup.name}
            type={
              lookup.lookup.type === 'number' ? 'number' :
              lookup.lookup.type === 'email' ? 'email' :
              lookup.lookup.type === 'date' ? 'date' :
              'text'
            }
            value={value}
            onChange={(e) => handleInputChange(fieldId, lookup.lookup.type === 'number' ? Number(e.target.value) : e.target.value)}
            required={lookup.required === 1}
            error={hasError}
            helperText={errorMessage}
            sx={{ mb: 2 }}
            InputLabelProps={lookup.lookup.type === 'date' ? { shrink: true } : undefined}
          />
        );

      case 'radio':
        return (
          <FormControl key={fieldId} error={hasError} sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {lookup.lookup.name}
              {lookup.required === 1 && ' *'}
            </Typography>
            <RadioGroup
              value={value}
              onChange={(e) => handleInputChange(fieldId, Number(e.target.value))}
            >
              {lookup.lookup.options.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio />}
                  label={[option.option, supplementSuffix(supplementForOption(lookup, option.id), t, fmtMoney)].filter(Boolean).join(' ')}
                />
              ))}
            </RadioGroup>
            {hasError && (
              <FormHelperText error>{errorMessage}</FormHelperText>
            )}
          </FormControl>
        );

      case 'checkbox': {
        const rawDescription = lookup.lookup.description ?? lookup.description;
        const hasDescription = !!(rawDescription && rawDescription.trim() !== '');
        const checkboxLabel = hasDescription ? rawDescription : lookup.lookup.name;
        return (
          <FormControl key={fieldId} error={hasError} sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={value === 1}
                  onChange={(e) => handleInputChange(fieldId, e.target.checked ? 1 : 0)}
                />
              }
              label={
                <Typography
                  component="span"
                  sx={{ whiteSpace: 'pre-wrap', ...(hasDescription && { fontSize: '0.875rem' }) }}
                >
                  {checkboxLabel}
                  {supplementForOption(lookup, null) && (
                    <strong> {supplementSuffix(supplementForOption(lookup, null), t, fmtMoney)}</strong>
                  )}
                </Typography>
              }
            />
            {hasError && (
              <FormHelperText error>{errorMessage}</FormHelperText>
            )}
          </FormControl>
        );
      }

      default:
        return null;
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      {!event.hide_public_title && (
      <Typography variant="h4" component="h1" gutterBottom>
        {event.name}
      </Typography>
      )}
      
      {event.description && (
        <Box 
          dangerouslySetInnerHTML={{ __html: event.description }} 
          sx={{ mb: 3, '& p': { mt: 0, mb: 1 } }}
        />
      )}

      {error && (
        <Alert ref={errorAlertRef} severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        {/* Customer information fields */}
        {!!event.group_registrations_enabled && (
          <FormControlLabel
            sx={{ mb: 1, display: 'flex' }}
            control={
              <Checkbox
                checked={formData.is_ente === true}
                onChange={(e) => setFormData(prev => ({ ...prev, is_ente: e.target.checked }))}
              />
            }
            label={t('form.ente_checkbox', 'Mi iscrivo per conto di un ente o azienda')}
          />
        )}
        <TextField
          fullWidth
          label={formData.is_ente ? t('form.denominazione', 'Denominazione ente') : t('form.nome', 'Nome')}
          type="text"
          value={formData.customer_name || ''}
          onChange={(e) => handleInputChange('customer_name', e.target.value)}
          required
          error={!!formErrors.customer_name}
          helperText={formErrors.customer_name}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={formData.is_ente ? t('form.riferimento', 'Riferimento (ufficio o persona)') : t('form.cognome', 'Cognome')}
          type="text"
          value={formData.customer_lastname || ''}
          onChange={(e) => handleInputChange('customer_lastname', e.target.value)}
          required
          error={!!formErrors.customer_lastname}
          helperText={formErrors.customer_lastname}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('form.email', 'Email')}
          type="email"
          value={formData.customer_email || ''}
          onChange={(e) => handleInputChange('customer_email', e.target.value)}
          required
          error={!!formErrors.customer_email}
          helperText={formErrors.customer_email}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          label={t('form.cf', 'Codice Fiscale')}
          type="text"
          value={formData.customer_fiscal_code || ''}
          onChange={(e) => {
            const val = e.target.value.toUpperCase();
            setFormData(prev => ({ ...prev, customer_fiscal_code: val }));
            if (val.length === 16 && !/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(val)) {
              setFormErrors(prev => ({ ...prev, customer_fiscal_code: t('form.err_cf', 'Codice fiscale non valido') }));
            } else {
              setFormErrors(prev => ({ ...prev, customer_fiscal_code: '' }));
            }
          }}
          onBlur={() => {
            const val = (formData.customer_fiscal_code as string) || '';
            if (val.length > 0 && val.length < 16) {
              setFormErrors(prev => ({ ...prev, customer_fiscal_code: t('form.err_cf_len', 'Il codice fiscale deve essere di 16 caratteri') }));
            } else if (val.length === 16 && !/^[A-Z]{6}\d{2}[A-Z]\d{2}[A-Z]\d{3}[A-Z]$/.test(val)) {
              setFormErrors(prev => ({ ...prev, customer_fiscal_code: t('form.err_cf', 'Codice fiscale non valido') }));
            }
          }}
          required
          error={!!formErrors.customer_fiscal_code}
          helperText={formErrors.customer_fiscal_code
            || (formData.is_ente ? t('form.cf_hint_ente', "Usa il codice fiscale personale di chi compila l'iscrizione") : undefined)}
          sx={{ mb: 2 }}
          inputProps={{ maxLength: 16, style: { textTransform: 'uppercase' } }}
        />

        {/* Participation mode selection */}
        {event.participation_mode === 'ENTRAMBI' && (
          <FormControl fullWidth error={!!formErrors.participation_mode} sx={{ mb: 2 }}>
            <InputLabel>{t('form.participation', 'Modalità di partecipazione')}</InputLabel>
            <Select
              value={formData.participation_mode || ''}
              label={t('form.participation', 'Modalità di partecipazione')}
              onChange={(e) => handleInputChange('participation_mode', e.target.value as string)}
              required
            >
              <MenuItem value="">
                <em>{t('form.select_mode', 'Seleziona modalità')}</em>
              </MenuItem>
              <MenuItem value="ONLINE">{t('form.online', 'Online')}</MenuItem>
              <MenuItem value="PRESENZA">{t('form.presenza', 'In presenza')}</MenuItem>
            </Select>
            {formErrors.participation_mode && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {formErrors.participation_mode}
              </Typography>
            )}
          </FormControl>
        )}

        {/* Dynamic fields */}
        {event.lookups
          .filter(l => l.participant_scope !== 'participants')
          .filter(l => !(l.show_if_ente === 1 && formData.is_ente !== true))
          .sort((a, b) => a.order - b.order)
          .map(renderField)}

        {/* Price selection */}
        {event.prices && event.prices.length > 0 && (
          <FormControl fullWidth error={!!formErrors.price_id} sx={{ mb: 2 }}>
            <InputLabel>{t('form.tipologia', 'Tipologia')}</InputLabel>
            <Select
              value={formData.price_id || ''}
              label={t('form.tipologia', 'Tipologia')}
              onChange={(e) => handleInputChange('price_id', e.target.value as number)}
              required
            >
              <MenuItem value="">
                <em>{t('form.select_tipologia', 'Seleziona tipologia')}</em>
              </MenuItem>
              {event.prices.map((price) => {
                const isDiscountValid = price.discounted_price
                  && price.valid_start_date
                  && new Date() < new Date(price.valid_start_date + 'T23:59:59');
                return (
                <MenuItem key={price.id} value={price.id}>
                  <Box>
                    <Typography variant="body1">{price.name}</Typography>
                    {price.gratis === 1 ? (
                      <Typography variant="caption" color="success.main">
                        {t('form.gratuito', 'Gratuito')}
                      </Typography>
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        €{isDiscountValid ? price.discounted_price : price.default_price}
                        {isDiscountValid && price.default_price !== price.discounted_price && (
                          <span style={{ textDecoration: 'line-through', marginLeft: 8 }}>
                            €{price.default_price}
                          </span>
                        )}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
                );
              })}
            </Select>
            {formErrors.price_id && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                {formErrors.price_id}
              </Typography>
            )}
          </FormControl>
        )}

        {/* Iscrizione di gruppo: numero persone (solo se abilitata sull'evento) */}
        {!!event.group_registrations_enabled && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>{t('form.group_size', 'Quante persone vuoi iscrivere?')}</InputLabel>
            <Select
              value={formData.group_size ?? 1}
              label={t('form.group_size', 'Quante persone vuoi iscrivere?')}
              onChange={(e) => handleInputChange('group_size', e.target.value as number)}
            >
              {Array.from({ length: 10 }, (_, i) => i + 1).map(n => (
                <MenuItem key={n} value={n}>{n === 1 ? t('form.group_solo_io', '1 — solo io') : t('form.group_n_persone', '{{n}} persone', { n })}</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Totale aggiornato in tempo reale: tipologia + supplementi scelti */}
        {(() => {
          const base = tipologiaAmount(event, formData.price_id as number);
          const supplements = computeSupplements(event, formData as any, formData.is_ente === true, false, base);
          if (!supplements.length || !formData.price_id) return null;
          const isGroup = Number(formData.group_size ?? 1) > 1;
          return (
            <Box sx={{ p: 2, mt: 1, backgroundColor: 'grey.100', borderRadius: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">{event.prices.find(p => p.id === formData.price_id)?.name}</Typography>
                <Typography variant="body2">{fmtMoney(base)}</Typography>
              </Box>
              {supplements.map(sup => (
                <Box key={sup.lookupId} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{sup.name}</Typography>
                  <Typography variant="body2">{signedMoney(sup.amount, fmtMoney)}</Typography>
                </Box>
              ))}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2">{t('riepilogo.totale_tuo', 'Totale')}</Typography>
                <Typography variant="subtitle2">{fmtMoney(personTotal(base, supplements))}</Typography>
              </Box>
              {isGroup && (
                <Typography variant="caption" color="text.secondary">
                  {t('riepilogo.gruppo_dopo', 'Il totale del gruppo si calcola nel passo successivo, con i dati dei partecipanti.')}
                </Typography>
              )}
            </Box>
          );
        })()}

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? <CircularProgress size={24} /> : t('form.submit', 'Invia Registrazione')}
        </Button>
      </Box>
    </Paper>
  );
};

export default DynamicForm;